import { env } from "@/config/env";
import api from "@/services/apiClient";
import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { getSignalRConnection } from "./signalRConnection";

let customerAlertsConnection: HubConnection | null = null;

/**
 * Returns the SignalR connection used for customer alert notifications.
 *
 * In production the negotiate endpoint returns an Azure SignalR URL + token so the client
 * connects directly to the service — alerts published by any API instance reach the customer.
 * In dev/test (no Azure SignalR configured) the endpoint returns a null URL and we fall back
 * to the shared in-memory hub connection.
 */
export async function getCustomerAlertsConnection(): Promise<HubConnection> {
  if (customerAlertsConnection) return customerAlertsConnection;

  try {
    const response = await api.post<{ url?: string | null; accessToken?: string | null }>(
      "Alerts/negotiate",
    );

    if (response.status === 200 && response.data?.url) {
      customerAlertsConnection = new HubConnectionBuilder()
        .withUrl(response.data.url, {
          accessTokenFactory: () => response.data.accessToken ?? "",
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(env.DEV ? LogLevel.Information : LogLevel.Error)
        .build();

      return customerAlertsConnection;
    }
  } catch (err) {
    console.warn(
      "[SignalR] Customer alerts negotiate failed — falling back to in-app hub",
      err,
    );
  }

  customerAlertsConnection = getSignalRConnection();
  return customerAlertsConnection;
}

export function resetCustomerAlertsConnection(): void {
  customerAlertsConnection = null;
}
